import { useState, useMemo, useCallback } from "react";
import { useDebounce } from "../../shared/hooks/useDebounce";
import { TranscriptData } from "./TranscriptViewer";

export interface TranscriptMatch {
  paragraphIndex: number;
  startOffset: number;
  length: number;
}

export function useTranscriptSearch(transcript: TranscriptData) {
  const [searchQuery, setSearchQuery] = useState("");
  const [currentMatchIndex, setCurrentMatchIndex] = useState(0);
  const debouncedQuery = useDebounce(searchQuery, 300);

  const matches = useMemo(() => {
    const q = debouncedQuery.toLowerCase().trim();
    if (!q || !transcript.paragraphs) return [];

    const result: TranscriptMatch[] = [];
    transcript.paragraphs.forEach((paragraph, paragraphIndex) => {
      const text = paragraph.sentences.map((s) => s.text).join(" ").toLowerCase();
      let pos = 0;
      while (true) {
        const idx = text.indexOf(q, pos);
        if (idx === -1) break;
        result.push({ paragraphIndex, startOffset: idx, length: q.length });
        pos = idx + 1;
      }
    });
    return result;
  }, [debouncedQuery, transcript.paragraphs]);

  // Reset current match index when matches change
  useMemo(() => {
    setCurrentMatchIndex(0);
  }, [matches]);

  const matchCount = matches.length;
  const isSearchActive = debouncedQuery.trim().length > 0;

  const goToNextMatch = useCallback(() => {
    if (matchCount === 0) return;
    setCurrentMatchIndex((prev) => (prev + 1) % matchCount);
  }, [matchCount]);

  const goToPreviousMatch = useCallback(() => {
    if (matchCount === 0) return;
    setCurrentMatchIndex((prev) => (prev - 1 + matchCount) % matchCount);
  }, [matchCount]);

  const clearSearch = useCallback(() => {
    setSearchQuery("");
    setCurrentMatchIndex(0);
  }, []);

  const getMatchesForParagraph = useCallback(
    (paragraphIndex: number) =>
      matches.filter((m) => m.paragraphIndex === paragraphIndex),
    [matches]
  );

  return {
    searchQuery,
    setSearchQuery,
    debouncedQuery,
    matches,
    matchCount,
    currentMatchIndex,
    goToNextMatch,
    goToPreviousMatch,
    clearSearch,
    isSearchActive,
    getMatchesForParagraph,
  };
}
